import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

import { useSearchParams } from "react-router-dom";

// Multi-lingual support
import { t } from "i18next";

import { styled } from "@mui/material/styles";

// Components
import BasicDetails from "../basicDetails/BasicDetails";
import CommunicationDetails from "../communicationDetails/CommunicationDetails";
import ConsentForm from "../consentForm/ConsentForm";
import FarmerQuestionnaire from "../farmerQuestionnaire/FarmerQuestionnaire";

// API Manager
import { getApi, postApi } from "../../../../../services/apiManager/ApiManager";

// AWS Manager
import {
  getMediaSignedData,
  uploadToAWSServer,
} from "../../../../../services/awsManager/AwsManager";

// Add update farmer types
import { IAddUpdateFarmer } from "./AddUpdateFarmerTypes";
import {
  EErrorCodes,
  EFarmerTabs,
} from "../../../../../components/shared/commonTypes/commonTypes";

// Data formatter
import {
  getFormattedDateForDisplay,
  getFormattedDateForServer,
} from "../../../../../components/shared/commonDataFormatter/CommonDataFormatter";

// Hooks
import { useToaster } from "../../../../../hooks/useToasterContext/useToasterContext";

// Component
import Loader from "../../../../../components/layout/Loader";
import dayjs from "dayjs";

// Utils
import { downloadFile } from "../../../../../utils/downloadFile/downloadFile";

// Styled Step Icon
const StepIcon = styled("div")<{ status: "completed" | "active" | "inactive" }>(
  ({ status }) => ({
    width: 30,
    height: 12,
    borderRadius: 6,
    backgroundColor:
      status === "completed"
        ? "#4D6A41"
        : status === "active"
          ? "#FBC69C"
          : "#E0E0E0",
    transition: "background-color 0.3s ease",
  }),
);

const AddUpdateFarmer = () => {
  // Check if url have query parameters for edit case
  const [searchParams] = useSearchParams();
  const farmerId = searchParams.get("farmerId");

  // Get and set farmer data
  const [farmerData, setFarmerData] = useState<any>({});

  // Stepper Steps
  const steps = [
    t("addUpdateFarmer.basicDetails"),
    t("addUpdateFarmer.communicationDetails"),
    t("addUpdateFarmer.consentForm"),
    t("addUpdateFarmer.complianceForm"),
  ];
  const [activeStep, setActiveStep] = useState(0);

  // API error codes
  const [apiErrorCode, setApiErrorCode] = useState<EErrorCodes | null>(null);

  // Content loading
  const [showLoader, setShowLoader] = useState(false);

  // Get and set newly added farmer id
  const [newlyAddedFarmerId, setNewlyAddedFarmerId] = useState<any>(null);

  // Toaster and loader
  const { setShowToaster } = useToaster();

  // Navigations
  const navigate = useNavigate();

  // Define the validateStepForm type explicitly
  const [validateStepForm, setValidateStepForm] = useState<any | null>(null);

  // Fetch farmer details in edit mode
  const fetchFarmerDetails = async (farmerId: string) => {
    try {
      // Set data loaded flag
      setShowLoader(true);
      // API parameter
      const apiParams = {
        id: farmerId,
      };

      // Get farmer details in edit case
      const response = await getApi("/v1/farmer/details", apiParams);
      if (response.status) {
        const farmerDetails = response.data ? response.data : {};
        if (farmerDetails) {
          farmerData.name = farmerDetails.name;
          farmerData.lastName = farmerDetails.lastName;
          farmerData.agentId = farmerDetails.agentId;
          farmerData.projectId = farmerDetails.projectId;
          farmerData.operationType = farmerDetails.operationType;
          farmerData.internalId = farmerDetails.internalId;
          farmerData.dateOfBirth = !farmerDetails.dateOfBirth
            ? null
            : dayjs(farmerDetails.dateOfBirth).toDate();
          farmerData.nationalId = farmerDetails.nationalId;
          farmerData.countryCode = farmerDetails.phoneCc;
          farmerData.mobileNumber = farmerDetails.phoneNumber;
          farmerData.address = farmerDetails.address;
          farmerData.village = farmerDetails.village;
          farmerData.subdistrict = farmerDetails.subDistrict;
          farmerData.district = farmerDetails.district;
          farmerData.province = farmerDetails.province;
          farmerData.postalcode = farmerDetails.postalCode;
          farmerData.mediaFileId = farmerDetails.mediaFileId;
          farmerData.consentFormId = farmerDetails.consentFormId;
        }

        // Get media content details
        if (farmerDetails.mediaFileId) {
          const mediaData = await getMediaSignedData(farmerDetails.mediaFileId);
          if (mediaData) {
            farmerData.selectedFarmerPhotoName = mediaData.fileName ?? "";
            farmerData.selectedFarmerPhotoSignedUrl =
              mediaData.s3SignedUrl ?? "";
          }
        }

        // Get consent form details
        if (farmerDetails.consentFormId) {
          const consentFormData = await getMediaSignedData(
            farmerDetails.consentFormId,
          );
          if (consentFormData) {
            farmerData.selectedConsentFormName = consentFormData.fileName ?? "";
            farmerData.selectedConsentFormSignedUrl =
              consentFormData.s3SignedUrl ?? "";
          }
        }
        // Set data loaded flag
        setShowLoader(false);
      }
    } catch (error) {
      console.error("Failed to fetch farmer data:", error);
    }
  };

  // Fetch farmer details in edit case
  useEffect(() => {
    if (farmerId) {
      fetchFarmerDetails(farmerId);
    } else {
      setShowLoader(false);
    }
  }, [farmerId]);

  // Handle next button click
  const handleNext = () => {
    if (validateStepForm) {
      validateStepForm();
    }
  };

  // Upload files to AWS server
  const uploadFiles = async (fileData: File): Promise<string | null> => {
    // Prepare upload request payload to get signed url to upload media
    const fileUploadRequest = {
      // fileId: "",
      fileName: fileData.name,
      fileMime: fileData.type,
      fileMeta: fileData,
    };

    const fileUploadResponse = await postApi(
      "/v1/file-store/upload-request",
      fileUploadRequest,
    );
    if (
      fileUploadResponse &&
      fileUploadResponse.status &&
      fileUploadResponse.data &&
      fileUploadResponse.data.s3SignedUrl
    ) {
      fileUploadResponse.data.fileId;
      await uploadToAWSServer(fileUploadResponse.data.s3SignedUrl, fileData);
      return fileUploadResponse.data.fileId;
    }
    return null;
  };

  // Handle farmer submit action
  const handleFarmerInformationSubmit = async (values: any) => {
    const updatedFarmerData = { ...farmerData, ...values };
    setFarmerData({ ...farmerData, ...values });
    // You can handle the data submission here if needed
    if (activeStep === steps.length - 2) {
      try {
        // Set data loaded flag
        setShowLoader(true);

        // Upload farmer photo
        if (updatedFarmerData && updatedFarmerData.selectedFarmerPhoto) {
          updatedFarmerData.mediaFileId = await uploadFiles(
            updatedFarmerData.selectedFarmerPhoto,
          );
        }

        // Upload farmer consent form
        if (updatedFarmerData && updatedFarmerData.selectedConsentForm) {
          updatedFarmerData.consentFormId = await uploadFiles(
            updatedFarmerData.selectedConsentForm,
          );
        }

        // Prepare add update farmer payload
        const addUpdateFarmerPayload = {
          name: updatedFarmerData.name,
          lastName: updatedFarmerData.lastName,
          agentId: updatedFarmerData.agentId,
          projectId: updatedFarmerData.projectId,
          operationType: updatedFarmerData.operationType,
          internalId: updatedFarmerData.internalId?.trim()
            ? updatedFarmerData.internalId.trim()
            : null,
          dateOfBirth: !updatedFarmerData.dateOfBirth
            ? null
            : dayjs(updatedFarmerData.dateOfBirth).format("YYYY-MM-DD"),
          nationalId: !(
            updatedFarmerData.nationalId && updatedFarmerData.nationalId.length
          )
            ? null
            : updatedFarmerData.nationalId,
          phoneCc: updatedFarmerData.countryCode,
          phoneNumber: updatedFarmerData.mobileNumber,
          address: updatedFarmerData.address,
          village: updatedFarmerData.village,
          subDistrict: updatedFarmerData.subdistrict,
          district: updatedFarmerData.district,
          province: updatedFarmerData.province,
          postalCode: updatedFarmerData.postalcode,
          mediaFileId: updatedFarmerData.mediaFileId,
          consentFormId: updatedFarmerData.consentFormId,
        } as IAddUpdateFarmer;

        let addUpdateFarmerResponse;
        if (!farmerId) {
          addUpdateFarmerResponse = await postApi(
            "/v1/farmer/add",
            addUpdateFarmerPayload,
          );
        } else {
          const finalUpdatePayload = {
            id: farmerId,
            ...addUpdateFarmerPayload,
          };
          addUpdateFarmerResponse = await postApi(
            "/v1/farmer/update",
            finalUpdatePayload,
          );
        }

        // Set newly added farmer id
        setNewlyAddedFarmerId(addUpdateFarmerResponse.data);

        if (
          !addUpdateFarmerResponse.status &&
          addUpdateFarmerResponse.code &&
          Object.values(EErrorCodes).includes(
            addUpdateFarmerResponse.code as EErrorCodes,
          )
        ) {
          setApiErrorCode(addUpdateFarmerResponse.code as EErrorCodes);

          if (
            addUpdateFarmerResponse.code ===
            EErrorCodes.E_FARMER_DUPLICATE_PHONENUMBER
          ) {
            setActiveStep(1);
          } else {
            setActiveStep(0);
          }
          return;
        }

        if (addUpdateFarmerResponse.status) {
          if (!farmerId) {
            setShowToaster(
              true,
              t("addUpdateFarmer.farmerAddedSuccessMessage"),
              "success",
            );
          } else {
            setShowToaster(
              true,
              t("addUpdateFarmer.farmerupdatedSuccessMessage"),
              "success",
            );
          }
          // navigate("/farmers");
        }
        setActiveStep(3);
      } catch (error) {
        setShowToaster(true, t("commonMessages.internalServerError"), "error");
      } finally {
        // Set data loaded flag
        setShowLoader(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  // Skip farmer questionnaire form
  const handleSkip = () => {
    navigate("/farmers");
  };

  // Handle field change event
  const handleFieldChange = () => {
    if (apiErrorCode) {
      setApiErrorCode(null);
    }
  };

  // Handle back button press
  const handleBack = () => {
    if (activeStep === steps.length - 1 || activeStep === 0) {
      navigate("/farmers?selectedActiveTab=" + EFarmerTabs.ENTITY_FARMER);
      return;
    }
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  // Function to render the current step component
  const renderStepContent = () => {
    // Common props for child component
    const commonProps = {
      setValidateStep: setValidateStepForm,
      onSubmit: handleFarmerInformationSubmit,
      onFieldChange: handleFieldChange,
      farmerData,
      apiErrorCode,
      onFileDownload: handleDownloadSelectedFile,
    };
    switch (activeStep) {
      case 0:
        return <BasicDetails {...commonProps} />;
      case 1:
        return <CommunicationDetails {...commonProps} />;
      case 2:
        return <ConsentForm {...commonProps} />;
      case 3:
        return (
          <FarmerQuestionnaire
            setValidateStep={setValidateStepForm}
            newlyAddedFarmerId={newlyAddedFarmerId}
          />
        );
      default:
        return null;
    }
  };

  const getNextButtonTitle = () => {
    if (activeStep === steps.length - 2)
      return !farmerId
        ? t("addUpdateFarmer.addFarmer")
        : t("addUpdateFarmer.updateFarmer");
    else if (activeStep === steps.length - 1)
      return t("addUpdateFarmer.submitFarmer");
    return t("addUpdateFarmer.next");
  };

  const getBackButtonTitle = () => {
    if (activeStep === steps.length - 1) return t("addUpdateFarmer.skip");
    return t("addUpdateFarmer.back");
  };

  // Handle download selected file
  const handleDownloadSelectedFile = (
    file: DownloadFile,
    fileName?: string,
  ) => {
    downloadFile(file, fileName);
  };

  return (
    <Box>
      <Box mt={2}>
        <Typography
          sx={{ color: "risinblack.main", fontWeight: 600, fontSize: 18 }}
        >
          {!farmerId
            ? t("addUpdateFarmer.newFarmer")
            : t("addUpdateFarmer.updateFarmer")}
        </Typography>
      </Box>
      <Box className="farmerdata-content" sx={{ margin: "20px auto" }}>
        {/* Page Title / Header */}
        <Box
          sx={{
            backgroundColor: "agricolors.drawerheader",
            display: "flex",
            alignItems: "center",
            pl: 2,
            pr: 3,
            height: 64,
            justifyContent: "space-between",
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
          }}
        >
          <Typography
            sx={{ color: "risinblack.main", fontWeight: 600, fontSize: 18 }}
          >
            {steps[activeStep]}
          </Typography>

          {/* Step Counter & Stepper */}
          {steps.length - 1 !== activeStep && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "farm.main",
                  fontWeight: "bold",
                  textAlign: "right",
                  mr: 2,
                }}
              >
                {t("addUpdateFarmer.step")} {activeStep + 1}{" "}
                {t("addUpdateFarmer.of")} {steps.length - 1}
              </Typography>

              <Box sx={{ mt: 1 }}>
                <Stepper
                  activeStep={activeStep}
                  alternativeLabel
                  connector={null}
                  sx={{ width: "100%" }}
                >
                  {steps.slice(0, -1).map((_, index) => (
                    <Step key={index}>
                      <StepLabel
                        StepIconComponent={() => (
                          <StepIcon
                            status={
                              index < activeStep
                                ? "completed"
                                : index === activeStep
                                  ? "active"
                                  : "inactive"
                            }
                          />
                        )}
                      />
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>
          )}
        </Box>

        {/* Step Content */}
        <Box
          className="farmerdata-mid-content"
          width={"100%"}
          p={3}
          sx={{
            backgroundColor: "farm.contrastText",
          }}
        >
          {showLoader && <Loader />}
          {!showLoader && renderStepContent()}
        </Box>

        {/* Navigation Buttons */}
        <Box
          sx={{
            backgroundColor: "agricolors.drawerheader",
            display: "flex",
            alignItems: "center",
            pl: 2,
            pr: 3,
            height: 64,
            justifyContent: "flex-end",
            gap: 2,
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10,
          }}
        >
          {/* <Button
            variant="outlined"
            onClick={handleBack}
            // color="risinblack"
          >
            {t("addUpdateFarmer.back")}
          </Button>
          {activeStep === 3 && (
            <Button
              variant="outlined"
              onClick={handleSkip}
              // color="risinblack"
            >
              {t("addUpdateFarmer.skip")}
            </Button>
          )}
          <Button variant="contained" onClick={handleNext}>
            {activeStep === 2 && farmerId && t("addUpdateFarmer.updateFarmer")}
            {activeStep === 2 && !farmerId && t("addUpdateFarmer.addFarmer")}
            {activeStep === 3 && t("addUpdateFarmer.finish")}
            {(activeStep === 0 || activeStep === 1) &&
              t("addUpdateFarmer.next")}
          </Button> */}
          <Button variant="outlined" onClick={handleBack}>
            {getBackButtonTitle()}
          </Button>
          <Button variant="contained" onClick={handleNext}>
            {getNextButtonTitle()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AddUpdateFarmer;
